import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CtcRefreshResponseDto,
  WbsRequestDto,
  WbsTreeResponse,
} from 'src/api/FiretrolApiClient';
import { wbsApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';

interface WbsSaveContext {
  previous: WbsTreeResponse | undefined;
}

export function useSaveJobWbs(jobId: number, successMessage?: string) {
  const qc = useQueryClient();
  const queryKey = queryKeys.wbs.forJob(jobId);

  return useMutation({
    mutationFn: (request: WbsRequestDto) =>
      wbsApiClient.updateJobWbs(jobId, request),
    meta: { successMessage },
    // The tree on screen already shows the edited dates; paint them into the
    // cache so a concurrent refetch can't flash the pre-edit values back.
    onMutate: async (request): Promise<WbsSaveContext> => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<WbsTreeResponse>(queryKey);
      if (previous) {
        qc.setQueryData<WbsTreeResponse>(queryKey, { ...previous, ...request });
      }
      return { previous };
    },
    onError: (_err, _request, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => invalidateFor('wbs.save', qc, { jobId }),
  });
}

const MAX_LISTED_CODES = 5;

function listCodes(codes: string[]): string {
  const listed = codes.slice(0, MAX_LISTED_CODES).join(', ');
  const rest = codes.length - MAX_LISTED_CODES;
  return rest > 0 ? `${listed} and ${rest} more` : listed;
}

export function buildCtcRefreshMessage({
  addedCodes,
  updatedCount,
  notInGpCodes,
}: CtcRefreshResponseDto): string {
  const parts: string[] = [];
  if (addedCodes.length > 0) parts.push(`Added ${listCodes(addedCodes)}.`);
  if (updatedCount > 0) {
    parts.push(
      `Updated hours on ${updatedCount} account${
        updatedCount === 1 ? '' : 's'
      }.`,
    );
  }
  if (parts.length === 0) parts.push('WBS is up to date with CTC.');
  if (notInGpCodes.length > 0) {
    parts.push(`Not in CTC: ${listCodes(notInGpCodes)}.`);
  }
  return parts.join(' ');
}

export function useRefreshJobWbsFromCtc(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => wbsApiClient.refreshJobCtcData(jobId),
    meta: {
      successMessageFn: (data) =>
        buildCtcRefreshMessage(data as CtcRefreshResponseDto),
    },
    onSettled: () => invalidateFor('wbsAccount.save', qc, { jobId }),
  });
}
